/**
 * Hook del detalle de una Membresía (US-2.4). La red vive en `membershipApi`; acá
 * solo el estado de la vista (loading / no encontrado / error / refresh).
 */
import { useCallback, useEffect, useState } from 'react';
import type { Membership } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { membershipApi } from '../api/membershipApi';

export interface UseMembershipResult {
  membership: Membership | null;
  loading: boolean;
  error: string | null;
  /** `true` cuando el backend responde 404 (membresía inexistente). */
  notFound: boolean;
  /** `true` cuando el backend responde 403 por falta de permisos. */
  forbidden: boolean;
  refresh: () => void;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudo cargar la membresía.';
}

export function useMembership(
  membershipId: string | undefined,
): UseMembershipResult {
  const [membership, setMembership] = useState<Membership | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!membershipId) {
      setLoading(false);
      setNotFound(true);
      setForbidden(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    setForbidden(false);

    membershipApi
      .get(membershipId)
      .then((result) => {
        if (!cancelled) setMembership(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setNotFound(true);
        } else if (caught instanceof ApiError && caught.status === 403) {
          setForbidden(true);
        } else {
          setError(toErrorMessage(caught));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [membershipId, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { membership, loading, error, notFound, forbidden, refresh };
}
